import React from 'react';

import { useIntl } from 'react-intl';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import {
	IconButton,
	type IconButtonAppearance,
	type IconButtonSpacing,
} from '@atlaskit/button/new';
import UploadIcon from '@atlaskit/icon/core/upload';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { getShareButtonLabel } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderMoreMenu/messages';

import { useShareUpToMessage } from '../../../../ui/share-conversation-modal/useShareUpToMessage';

import messages from './shareUpToMessageMessages';

export type ShareUpToMessageButtonProps = {
	/**
	 * Id of the message this button shares up to. The generated share
	 * includes every message in the conversation up to and including it.
	 */
	messageId: string;
	appearance?: IconButtonAppearance;
	spacing?: IconButtonSpacing;
	shape?: 'default' | 'circle';
	testId?: string;
};

/**
 * Per-message "share chat up to this point" affordance shown alongside the copy button.
 *
 * Opens the shared-snapshot modal anchored to `messageId` via the
 * `ShareUpToMessage` context. Renders nothing when the context is absent
 * (e.g. embeddings without the share flow) or when `messageId` is empty, so
 * the feature degrades gracefully. When the message is not yet shareable,
 * the button remains visible but disabled.
 */
export const ShareUpToMessageButton = ({
	messageId,
	appearance = 'subtle',
	spacing = 'default',
	shape = 'default',
	testId,
}: ShareUpToMessageButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const shareContext = useShareUpToMessage();

	// Hide when sharing is unavailable or the id is missing. Once the sharing
	// feature is present, keep the action visible and disable it until this
	// message becomes a shareable boundary, matching the header share action.
	if (!shareContext || !messageId) {
		return null;
	}

	// Past the guard `shareContext` is guaranteed non-null, so the handler can
	// call `shareUpToMessage` directly.
	const { shareUpToMessage, isMessageShareable, shareDisabledReason } = shareContext;
	const isDisabled = !isMessageShareable(messageId);
	const label = isDisabled
		? getShareButtonLabel(shareDisabledReason ?? 'missing-valid-response', formatMessage)
		: formatMessage(messages.shareUpToMessageLabel);
	const onClick = (_event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		sendAnalyticsEvent(analyticsEvent.update({ actionSubjectId: 'shareUpToMessageButton' }));
		shareUpToMessage(messageId);
	};

	return (
		<IconButton
			isTooltipDisabled={false}
			type="button"
			onClick={onClick}
			isDisabled={isDisabled}
			icon={UploadIcon}
			appearance={appearance}
			spacing={spacing}
			label={label}
			shape={shape}
			testId={testId}
		/>
	);
};
